import { useCallback, useEffect, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { HostedAgentSourceList } from "@/api/client/hosted-agents-sources"
import type { SessionType } from "@/types"

import { ApiError } from "@/api/client/_shared"
import { uploadHostedAgentSourceApi } from "@/api/client/hosted-agents-sources"
import { logger } from "@/lib/logger"
import { ChatService } from "@/services/chat-service"

type UseAgentFileUploadParams = {
  agentRef: string
  sessionType: SessionType
  sessionId: string | null
  ensureSession: () => Promise<void>
  resetFiles: () => void
  onSessionEnsured?: (sessionId: string) => boolean | void | Promise<boolean | void>
}

/**
 * Task sources use the local backend parser in cloud AND on-prem. A completed
 * request means extracted text is ready for source_read, not merely queued for
 * an external index. Files never ride the durable turn or reach model URLs.
 */
export function useAgentFileUpload({
  agentRef,
  sessionType,
  ensureSession,
  resetFiles,
  onSessionEnsured,
}: UseAgentFileUploadParams) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isUploading, setIsUploading] = useState(false)
  const uploadingRef = useRef(false)
  const activeAgentRef = useRef(agentRef)
  activeAgentRef.current = agentRef
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  // A stable key per File survives retries, including an uncertain HTTP response.
  // Keys carry the target session: never reuse across threads. A successful
  // receipt must still be validated server-side on retry: its source may have
  // been removed in the meantime, including from another tab.
  const requests = useRef(new WeakMap<File, Map<string, { key: string }>>())

  const uploadPendingFiles = useCallback(
    async (files: File[]): Promise<{ ok: boolean; sessionId: string | null }> => {
      let targetSessionId = ChatService.getInstance().getOpenSession(sessionType)?.id ?? null
      const isCurrentTarget = () =>
        mountedRef.current &&
        activeAgentRef.current === agentRef &&
        ChatService.getInstance().getOpenSession(sessionType)?.id === targetSessionId
      if (files.length === 0) return { ok: true, sessionId: targetSessionId }
      if (uploadingRef.current) return { ok: false, sessionId: targetSessionId }
      uploadingRef.current = true
      setIsUploading(true)
      try {
        await ensureSession()
        const ensuredSessionId = ChatService.getInstance().getOpenSession(sessionType)?.id ?? null
        // An existing target cannot change while ensureSession is pending.
        // Only the greeting's null -> newly-created session transition is valid.
        if (targetSessionId && ensuredSessionId !== targetSessionId)
          return { ok: false, sessionId: targetSessionId }
        targetSessionId = ensuredSessionId
        if (!targetSessionId) throw new Error("Session was not created")
        if (!isCurrentTarget()) return { ok: false, sessionId: targetSessionId }
        // The old greeting URL can still close the speculative session during
        // React's next effect flush. Wait for the owning router to adopt it
        // before any upload or draft recovery uses that session.
        const adopted = await onSessionEnsured?.(targetSessionId)
        if (adopted === false || !isCurrentTarget())
          return { ok: false, sessionId: targetSessionId }
        const scope = `${agentRef}/${targetSessionId}`

        for (const file of files) {
          // Navigating away stops this batch; never upload or send into a new thread.
          if (!isCurrentTarget()) {
            return { ok: false, sessionId: targetSessionId }
          }
          let perScope = requests.current.get(file)
          if (!perScope) {
            perScope = new Map()
            requests.current.set(file, perScope)
          }
          let receipt = perScope.get(scope)
          if (!receipt) {
            receipt = { key: crypto.randomUUID() }
            perScope.set(scope, receipt)
          }
          const source = await uploadHostedAgentSourceApi(agentRef, targetSessionId, {
            file,
            requestId: receipt.key,
          })
          queryClient.setQueryData<HostedAgentSourceList>(
            ["hosted-agent-sources", agentRef, targetSessionId],
            (old) =>
              old
                ? {
                    ...old,
                    sources: [...old.sources.filter((entry) => entry.id !== source.id), source],
                  }
                : old
          )
          if (source.status !== "ready") throw new Error("Source is not ready")
        }
        await queryClient.invalidateQueries({
          queryKey: ["hosted-agent-sources", agentRef, targetSessionId],
        })
        if (!isCurrentTarget()) return { ok: false, sessionId: targetSessionId }
        resetFiles()
        return { ok: true, sessionId: targetSessionId }
      } catch (error) {
        logger.error("Hosted-agent source upload failed", error as Error)
        let errorKey = "agents.chat.upload_failed"
        if (error instanceof ApiError && error.status === 403)
          errorKey = "agents.chat.upload_forbidden"
        if (error instanceof ApiError && [413, 415, 422].includes(error.status))
          errorKey = "agents.sources.unsupported"
        if (isCurrentTarget()) toast.error(t(errorKey), { position: "top-center" })
        return { ok: false, sessionId: targetSessionId }
      } finally {
        uploadingRef.current = false
        setIsUploading(false)
      }
    },
    [agentRef, ensureSession, onSessionEnsured, queryClient, resetFiles, sessionType, t]
  )

  return { isUploading, uploadPendingFiles }
}
